import React, { useEffect, useState, useCallback } from "react";
import {
  UsersIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";
import CustomTableComponent from "../../components/CustomTableComponent";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};
const modalAnimation = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

export default function AdminPartnerStatisticCard() {
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPartners = useCallback(async () => {
    try {
      const res = await ApiCustom.get("/company-shows");
      const data = res?.data?.data?.data?.users || [];
      const count = data.filter(
        (p) => p.approvalStatus == "Gửi duyệt" || p.approvalStatus == "Từ chối",
      ).length;
      setTotal(count);
    } catch {
      setTotal(0);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="cursor-pointer transition-all p-5 rounded-2xl bg-white border border-purple-100 shadow-md"
        onClick={() => setModalOpen(true)}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className="p-3 bg-purple-100 rounded-full mb-1 flex items-center justify-center">
            <UsersIcon className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <p className="text-black text-lg">Hội viên mới chờ duyệt</p>
            <p className="text-3xl font-semibold mt-1">{total}</p>
          </div>
        </div>
      </motion.div>
      <ApprovePartnerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onChanged={fetchPartners}
      />
    </div>
  );
}

function ApprovePartnerModal({ open, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("Tất cả");
  const [approving, setApproving] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackId, setFeedbackId] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiCustom.get("/company-shows");
      const users = res?.data?.data?.data?.users || [];
      setList(
        users.filter(
          (u) =>
            u.approvalStatus === "Gửi duyệt" || u.approvalStatus === "Từ chối",
        ),
      );
    } catch {
      toast.error("Lỗi khi lấy danh sách hội viên");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleRowClick = async (item) => {
    setDetailLoading(true);
    try {
      const res = await ApiCustom.get(`/company-show-by-id/${item.id}`);
      setSelectedPartner(res?.data?.data);
      setDetailOpen(true);
    } catch {
      toast.error("Không thể lấy chi tiết hội viên");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setApproving(id);
    try {
      await ApiCustom.put(`/users/${id}/approve`, {
        approvalStatus: "Đã duyệt",
      });

      await fetchList();
      setConfirmId(null);
      if (onChanged) onChanged();

      toast.success("Đã duyệt hội viên!");
    } catch {
      toast.error("Duyệt thất bại!");
    } finally {
      setApproving(null);
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackNote.trim()) return;
    setSendingFeedback(true);
    try {
      await ApiCustom.patch(`/company-feedback/${feedbackId}`, {
        note: feedbackNote,
      });
      toast.success("Đã gửi phản hồi cho người gửi duyệt!");
      setFeedbackOpen(false);
      setFeedbackId(null);
      setFeedbackNote("");
      await fetchList();
      if (onChanged) onChanged();
    } catch {
      toast.error("Gửi phản hồi thất bại!");
    } finally {
      setSendingFeedback(false);
    }
  };

  const filteredList = list.filter((p) =>
    filter === "Tất cả"
      ? true
      : filter === "Đang chờ duyệt"
        ? p.approvalStatus === "Gửi duyệt"
        : filter === "Đã phản hồi"
          ? p.approvalStatus === "Từ chối"
          : true,
  );

  const headers = [
    {
      key: "userAvatar",
      label: "Logo",
      render: (item) => (
        <div className="w-10 h-10 rounded-lg overflow-hidden mx-auto bg-gray-50">
          <img
            src={
              item?.userAvatar ||
              `https://ui-avatars.com/api/?name=${item?.companyName}&size=128&background=random`
            }
            alt={item?.companyName}
            className="w-full h-full object-contain"
          />
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Tên đơn vị",
      render: (item) => <span className="font-medium">{item.companyName}</span>,
    },
    {
      key: "userEmail",
      label: "Email",
      render: (item) => <span>{item.userEmail || "----"}</span>,
    },
    {
      key: "userPhone",
      label: "Số điện thoại",
      render: (item) => item.userPhone || "----",
    },
    {
      key: "approvalStatus",
      label: "Trạng thái",
      render: (item) => (
        <StatusBadge
          approvalStatus={
            item.approvalStatus === "Từ chối"
              ? "Đã phản hồi"
              : item.approvalStatus === "Gửi duyệt"
                ? "Đang chờ duyệt"
                : ""
          }
          note={item.note}
        />
      ),
    },
    {
      key: "actions",
      label: "Thao tác",
      render: (item) => {
        if (item.approvalStatus === "Gửi duyệt") {
          return (
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-outline btn-warning btn-xs flex items-center gap-1"
                title="Phản hồi hội viên"
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedbackId(item.id);
                  setFeedbackOpen(true);
                  setFeedbackNote(item.note || "");
                }}
              >
                Phản hồi
              </button>
              <button
                className="btn btn-outline btn-success btn-xs flex items-center gap-1"
                disabled={!!approving}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmId(item.id);
                }}
              >
                Duyệt
              </button>
            </div>
          );
        }
        if (item.approvalStatus === "Từ chối") {
          return (
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-outline btn-warning btn-xs flex items-center gap-1"
                title="Xem/Sửa phản hồi"
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedbackId(item.id);
                  setFeedbackOpen(true);
                  setFeedbackNote(item.note || "");
                }}
              >
                <span>Xem phản hồi</span>
              </button>
              <button
                className="btn btn-outline btn-success btn-xs flex items-center gap-1"
                disabled={!!approving}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmId(item.id);
                }}
              >
                Duyệt
              </button>
            </div>
          );
        }
        return null;
      },
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...modalAnimation}
          className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center"
        >
          <motion.div
            {...modalAnimation}
            className="bg-white rounded-xl w-full max-w-7xl mx-4 p-6 border border-gray-200 relative shadow-xl"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
              onClick={onClose}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <div className="flex justify-between items-center mb-4 mr-4">
              <h2 className="text-xl font-bold">Hội viên mới chờ duyệt</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border rounded px-3 py-2 text-base focus:ring focus:border-indigo-400 shadow-sm bg-white"
                style={{ minWidth: 180 }}
              >
                <option value="Tất cả">Tất cả</option>
                <option value="Đang chờ duyệt">Đang chờ duyệt</option>
                <option value="Đã phản hồi">Đã phản hồi</option>
              </select>
            </div>
            {loading ? (
              <div className="py-8 text-center text-gray-500">
                Đang tải dữ liệu...
              </div>
            ) : (
              <>
                <CustomTableComponent
                  headers={headers}
                  data={filteredList}
                  onRowClick={confirmId ? () => {} : handleRowClick}
                />
                {filteredList.length === 0 && (
                  <div className="py-6 text-center text-gray-400 text-base">
                    Không có hội viên mới chờ duyệt.
                  </div>
                )}
              </>
            )}

            <FeedbackModal
              open={feedbackOpen}
              onClose={() => setFeedbackOpen(false)}
              onSubmit={handleSendFeedback}
              note={feedbackNote}
              setNote={setFeedbackNote}
              loading={sendingFeedback}
            />

            <MemberDetailModal
              open={detailOpen}
              loading={detailLoading}
              onClose={() => setDetailOpen(false)}
              partner={selectedPartner}
            />

            {confirmId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xs relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                    onClick={() => setConfirmId(null)}
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  <div className="text-lg mb-3 font-semibold">
                    Xác nhận duyệt?
                  </div>
                  <div className="text-gray-500 mb-4 text-sm">
                    Bạn chắc chắn muốn duyệt hội viên <br />
                    <b>{list.find((i) => i.id === confirmId)?.companyName}</b>?
                  </div>
                  <div className="flex justify-end space-x-2 mt-2">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setConfirmId(null)}
                    >
                      Hủy
                    </button>
                    <button
                      className={`btn btn-success btn-sm px-4 ${
                        approving ? "opacity-60 pointer-events-none" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleApprove(confirmId);
                      }}
                    >
                      {approving === confirmId ? "Đang duyệt..." : "Duyệt"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatusBadge({ approvalStatus, note }) {
  let cls = "px-3 py-1 rounded-full text-sm whitespace-nowrap ";
  if (approvalStatus === "Đang chờ duyệt") {
    cls += "bg-yellow-100 text-yellow-800";
    return <span className={cls}>Đang chờ duyệt</span>;
  }
  if (approvalStatus === "Đã phản hồi") {
    cls += "bg-yellow-100 text-yellow-900";
    return (
      <span className={cls} title={note && note.trim() ? note : undefined}>
        Đã phản hồi
      </span>
    );
  }
  return null;
}

function FeedbackModal({ open, onClose, onSubmit, note, setNote, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl relative">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
          onClick={onClose}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <div className="text-xl mb-3 font-bold flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="w-6 h-6" />
          Phản hồi thông tin hội viên
        </div>
        <textarea
          className="w-full p-2 border rounded mb-4"
          rows={8}
          placeholder="Nhập lý do hoặc góp ý..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={loading}
        />
        <div className="flex justify-end space-x-2 mt-2">
          <button
            className="btn btn-outline btn-sm"
            onClick={onClose}
            disabled={loading}
          >
            Đóng
          </button>
          <button
            className={`btn btn-warning btn-sm px-4 ${
              loading ? "opacity-60 pointer-events-none" : ""
            }`}
            onClick={onSubmit}
            disabled={loading || !note.trim()}
          >
            {loading ? "Đang gửi..." : "Gửi phản hồi"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberDetailModal({ open, loading, onClose, partner }) {
  if (!open || !partner) return null;

  const representative = partner?.users?.find(
    (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
  );

  function formatDate(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString("vi-VN");
  }

  const InfoItem = ({ label, value }) => (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-sm text-gray-800">{value || "----"}</p>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-3xl mx-4 rounded-xl p-6 border border-gray-200 overflow-y-auto"
          style={{ maxHeight: "90vh" }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Chi tiết Hội viên
          </h2>
          {loading ? (
            <p className="text-gray-500">Đang tải thông tin...</p>
          ) : (
            <div className="space-y-7 text-[15px] text-gray-800">
              <section>
                <div className="flex items-center gap-5 mb-3">
                  <img
                    src={
                      partner.userAvatar ||
                      `https://ui-avatars.com/api/?name=${partner.companyName}`
                    }
                    alt="Logo"
                    className="w-20 h-20 rounded-2xl border bg-gray-100 object-contain"
                  />
                  <div>
                    <div className="font-bold text-2xl">
                      {partner.companyName}
                    </div>
                    <div className="text-sm text-gray-500">
                      Tên tiếng Anh:{" "}
                      <span className="font-normal">
                        {partner.companyNameEn || "----"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Tên viết tắt:{" "}
                      <span className="font-normal">
                        {partner.companyShortName || "----"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InfoItem label="Mã số thuế" value={partner.taxCode} />
                  <InfoItem
                    label="Ngày thành lập"
                    value={formatDate(partner.establishedDate)}
                  />
                  <InfoItem
                    label="Địa chỉ đơn vị"
                    value={partner.businessAddress}
                  />
                  <InfoItem
                    label="Điện thoại đơn vị"
                    value={partner.userPhone}
                  />
                  <InfoItem label="Email đơn vị" value={partner.userEmail} />
                  <InfoItem
                    label="Địa chỉ website"
                    value={partner.websiteOrHotline}
                  />
                  <InfoItem
                    label="Ngành nghề kinh doanh"
                    value={partner.businessField}
                  />
                </div>
              </section>

              <hr className="border-t border-gray-200 my-5" />
              <section>
                <h3 className="font-semibold text-lg mb-2">
                  Tổng Giám đốc / Giám đốc
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <InfoItem label="Họ và tên" value={partner.ceoFullName} />
                  <InfoItem
                    label="Sinh nhật"
                    value={formatDate(partner.ceoDateOfBirth)}
                  />
                  <InfoItem
                    label="Số điện thoại liên hệ"
                    value={partner.ceoPhone}
                  />
                  <InfoItem label="Email" value={partner.ceoEmail} />
                </div>
              </section>

              <hr className="border-t border-gray-200 my-5" />
              <section>
                <h3 className="font-semibold text-lg mb-2">Người đại diện</h3>
                {representative ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <InfoItem
                      label="Họ và tên"
                      value={representative.userFullName}
                    />
                    <InfoItem
                      label="Sinh nhật"
                      value={formatDate(representative.dateOfBirth)}
                    />
                    <InfoItem
                      label="Số điện thoại liên hệ"
                      value={representative.userPhone}
                    />
                    <InfoItem label="Email" value={representative.userEmail} />
                    <InfoItem
                      label="Chức vụ"
                      value={
                        representative?.CompaniesHasUsers?.userPosition || ""
                      }
                    />
                  </div>
                ) : (
                  <div className="text-gray-500">Chưa có người đại diện</div>
                )}
              </section>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}